import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, input, output, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import { MatAnchor, MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { saveAs } from 'file-saver';
import { fromEvent, of } from 'rxjs';
import { catchError, filter, map, switchMap } from 'rxjs/operators';
import { ApiService } from 'src/app/services/api.service';
import { GoogleChatCustomer } from 'src/app/models/google-chat-customer.model';
import { MicrosoftTenant } from 'src/app/models/microsoft-tenant.model';

@Component({
  selector: 'app-company-detail-slack-section',
  templateUrl: './company-detail-slack-section.component.html',
  styleUrl: './company-detail-slack-section.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatButton, MatAnchor, MatIcon, RouterLink],
})
export class CompanyDetailSlackSectionComponent {
  private readonly api = inject(ApiService);
  private readonly destroy_ref = inject(DestroyRef);

  readonly company_id = input.required<string>();
  readonly slack_external_tenant_id = input<string | null>(null);
  readonly can_edit = input(false);
  readonly tenant_saved = output<string | null>();

  readonly connecting = signal(false);
  readonly disconnecting = signal(false);
  readonly downloading_teams_package = signal(false);

  readonly is_connected = computed((): boolean => {
    const tenant_id = this.slack_external_tenant_id();

    return tenant_id != null && tenant_id !== '';
  });

  readonly workspace_url = computed((): string | null => {
    const tenant_id = this.slack_external_tenant_id();
    if (tenant_id == null || tenant_id === '') {
      return null;
    }

    return `https://app.slack.com/client/${tenant_id}`;
  });

  readonly fields_disabled = computed((): boolean => !this.can_edit());

  private readonly company_id$ = toObservable(this.company_id);

  readonly microsoft_tenants = toSignal(
    this.company_id$.pipe(
      switchMap((company_id: string) =>
        this.api
          .getMicrosoftTenantsPaged({
            company_id,
            page: 1,
            per_page: 50,
          })
          .pipe(
            map((response): MicrosoftTenantRow[] =>
              (response.data ?? []).map(
                (tenant: MicrosoftTenant): MicrosoftTenantRow => ({
                  id: tenant.id,
                  tenant_id: tenant.tenant_id,
                  label: tenant.name?.trim() || tenant.tenant_id,
                })
              )
            ),
            catchError(() => of([] as MicrosoftTenantRow[]))
          )
      )
    ),
    { initialValue: [] as MicrosoftTenantRow[] }
  );

  readonly google_chat_customers = toSignal(
    this.company_id$.pipe(
      switchMap((company_id: string) =>
        this.api
          .getGoogleChatCustomersPaged({
            company_id,
            page: 1,
            per_page: 50,
          })
          .pipe(
            map((response): GoogleChatCustomerRow[] =>
              (response.data ?? []).map(
                (customer: GoogleChatCustomer): GoogleChatCustomerRow => ({
                  id: customer.id,
                  label: googleChatCustomerLabel(customer),
                })
              )
            ),
            catchError(() => of([] as GoogleChatCustomerRow[]))
          )
      )
    ),
    { initialValue: [] as GoogleChatCustomerRow[] }
  );

  readonly has_teams_tenant = computed((): boolean => this.microsoft_tenants().length > 0);

  readonly has_google_customer = computed((): boolean => this.google_chat_customers().length > 0);

  constructor() {
    fromEvent(window, 'focus')
      .pipe(
        takeUntilDestroyed(this.destroy_ref),
        filter((): boolean => this.connecting())
      )
      .subscribe((): void => this.refresh_connection_after_oauth());
  }

  connect(): void {
    if (!this.can_edit() || this.connecting() || this.is_connected()) {
      return;
    }

    this.connecting.set(true);
    this.api.startSlackBotInstallOAuth(this.company_id()).subscribe({
      next: (response): void => {
        window.open(response.authorization_url, '_blank', 'noopener,noreferrer');
      },
      error: (): void => {
        this.connecting.set(false);
      },
    });
  }

  disconnect(): void {
    if (!this.can_edit() || this.disconnecting() || !this.is_connected()) {
      return;
    }

    if (!confirm('¿Desconectar Slack de esta empresa?')) {
      return;
    }

    this.disconnecting.set(true);
    this.api.disconnectCompanySlack(this.company_id()).subscribe({
      next: (company): void => {
        this.disconnecting.set(false);
        this.tenant_saved.emit(company.slack_external_tenant_id ?? null);
      },
      error: (): void => {
        this.disconnecting.set(false);
      },
    });
  }

  open_workspace(): void {
    const url = this.workspace_url();
    if (url == null) {
      return;
    }

    window.open(url, '_blank', 'noopener,noreferrer');
  }

  download_teams_app_package(): void {
    if (this.downloading_teams_package()) {
      return;
    }

    this.downloading_teams_package.set(true);
    this.api.downloadTeamsAppPackage().subscribe({
      next: (response): void => {
        this.downloading_teams_package.set(false);
        if (response.body == null) {
          return;
        }

        saveAs(response.body, 'bisual-teams-app.zip');
      },
      error: (): void => {
        this.downloading_teams_package.set(false);
      },
    });
  }

  private refresh_connection_after_oauth(): void {
    this.api.getCompany(this.company_id()).subscribe({
      next: (company): void => {
        this.connecting.set(false);
        this.tenant_saved.emit(company.slack_external_tenant_id ?? null);
      },
      error: (): void => {
        this.connecting.set(false);
      },
    });
  }
}

interface MicrosoftTenantRow {
  id: number;
  tenant_id: string;
  label: string;
}

interface GoogleChatCustomerRow {
  id: number;
  label: string;
}

function googleChatCustomerLabel(customer: GoogleChatCustomer): string {
  return (
    customer.display_name?.trim() ||
    customer.primary_email_domain?.trim() ||
    customer.customer_id?.trim() ||
    customer.domain_id?.trim() ||
    `#${customer.id}`
  );
}
